import type { Feeling, Superpower } from '@fyf/shared';
import type { Dispatch, ReactNode } from 'react';
import { createContext, useContext, useReducer } from 'react';

export type Screen =
  | 'intro'
  | 'spark'
  | 'superpower'
  | 'feeling'
  | 'contact'
  | 'generating'
  | 'reveal'
  | 'share';

export interface CaptureState {
  screen: Screen;
  surfaceTag: string | null;
  referralCode: string | null;
  freeText: string;
  superpower: Superpower | null;
  feeling: Feeling | null;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  organization: string;
  submissionId: string | null;
  flameImageUrl: string | null;
  surfaceName: string | null;
  eventHashtag: string | null;
  error: string | null;
}

export const initialState: CaptureState = {
  screen: 'intro',
  surfaceTag: null,
  referralCode: null,
  freeText: '',
  superpower: null,
  feeling: null,
  firstName: '',
  lastName: '',
  displayName: '',
  email: '',
  organization: '',
  submissionId: null,
  flameImageUrl: null,
  surfaceName: null,
  eventHashtag: null,
  error: null,
};

export type CaptureAction =
  | { type: 'SET_FREE_TEXT'; payload: string }
  | { type: 'SET_SUPERPOWER'; payload: Superpower }
  | { type: 'SET_FEELING'; payload: Feeling }
  | {
      type: 'SET_CONTACT';
      payload: {
        firstName: string;
        lastName: string;
        displayName: string;
        email: string;
        organization: string;
      };
    }
  | { type: 'SET_SCREEN'; payload: Screen }
  | { type: 'SET_SURFACE_TAG'; payload: string }
  | { type: 'SET_REFERRAL_CODE'; payload: string }
  | { type: 'SET_SUBMISSION_ID'; payload: string }
  | { type: 'SET_FLAME_IMAGE_URL'; payload: string }
  | { type: 'SET_SURFACE_NAME'; payload: string }
  | { type: 'SET_EVENT_HASHTAG'; payload: string }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'RESET' };

export function captureReducer(state: CaptureState, action: CaptureAction): CaptureState {
  switch (action.type) {
    case 'SET_FREE_TEXT':
      return { ...state, freeText: action.payload };
    case 'SET_SUPERPOWER':
      return { ...state, superpower: action.payload };
    case 'SET_FEELING':
      return { ...state, feeling: action.payload };
    case 'SET_CONTACT':
      return { ...state, ...action.payload };
    case 'SET_SCREEN':
      return { ...state, screen: action.payload };
    case 'SET_SURFACE_TAG':
      return { ...state, surfaceTag: action.payload };
    case 'SET_REFERRAL_CODE':
      return { ...state, referralCode: action.payload };
    case 'SET_SUBMISSION_ID':
      return { ...state, submissionId: action.payload };
    case 'SET_FLAME_IMAGE_URL':
      return { ...state, flameImageUrl: action.payload, error: null };
    case 'SET_SURFACE_NAME':
      return { ...state, surfaceName: action.payload };
    case 'SET_EVENT_HASHTAG':
      return { ...state, eventHashtag: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const CaptureFlowContext = createContext<CaptureState | null>(null);
const CaptureDispatchContext = createContext<Dispatch<CaptureAction> | null>(null);

export function CaptureFlowProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(captureReducer, initialState);
  return (
    <CaptureFlowContext.Provider value={state}>
      <CaptureDispatchContext.Provider value={dispatch}>{children}</CaptureDispatchContext.Provider>
    </CaptureFlowContext.Provider>
  );
}

export function useCaptureState(): CaptureState {
  const ctx = useContext(CaptureFlowContext);
  if (!ctx) throw new Error('useCaptureState must be used within CaptureFlowProvider');
  return ctx;
}

export function useCaptureDispatch(): Dispatch<CaptureAction> {
  const ctx = useContext(CaptureDispatchContext);
  if (!ctx) throw new Error('useCaptureDispatch must be used within CaptureFlowProvider');
  return ctx;
}
